import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Wand2, Plus, BookOpen, Check, Shuffle } from "lucide-react";
import { uid, autoGroups, shuffleIds } from "@/lib/constants";
import { initials } from "@/lib/scoring";
import CourseLibraryModal from "@/screens/CourseLibraryModal";

const STEPS = ["Name", "Format", "Course", "Groups", "Side games"];

const FORMATS = [
  { id: "stableford", label: "Stableford", desc: "Points per hole (net) — higher is better" },
  { id: "par", label: "Par", desc: "+1 win · 0 halve · −1 loss vs net par" },
  { id: "stroke", label: "Stroke", desc: "Lowest total net strokes wins" },
  { id: "parPlus", label: "Par Plus", desc: "Like Par, with bonus for net birdies & eagles" },
];

/**
 * Step-by-step wizard to create a new day.
 * Props:
 *  - open, onClose
 *  - event, updateEventLocal
 *  - onCreated(dayId) — called after commit
 */
export default function NewDayWizard({ open, onClose, event, updateEventLocal, onCreated }) {
  const [step, setStep] = useState(1);
  const [label, setLabel] = useState("");
  const [date, setDate] = useState("");
  const [format, setFormat] = useState("stableford");
  const [courseKey, setCourseKey] = useState("");
  const [groupSizePref, setGroupSizePref] = useState(4);
  const [groups, setGroups] = useState([]);
  const [bbbEnabled, setBbbEnabled] = useState(true);
  const [skinsMode, setSkinsMode] = useState("off");
  const [skinsValue, setSkinsValue] = useState(0);
  const [nassauMode, setNassauMode] = useState("off");
  const [nassauValue, setNassauValue] = useState(0);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseSubtitle, setNewCourseSubtitle] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);

  const players = event.players || [];

  useEffect(() => {
    if (open) {
      setStep(1);
      setLabel(`Day ${(event.days?.length || 0) + 1}`);
      setDate("");
      setFormat("stableford");
      const firstKey = Object.keys(event.courses || {})[0] || "";
      setCourseKey(firstKey);
      setGroupSizePref(4);
      setGroups([]);
      setBbbEnabled(true);
      setSkinsMode("off"); setSkinsValue(0);
      setNassauMode("off"); setNassauValue(0);
      setShowNewCourse(false);
      setNewCourseName(""); setNewCourseSubtitle("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const assignedIds = new Set(groups.flatMap((g) => g.playerIds));
  const unassigned = players.filter((p) => !assignedIds.has(p.id));

  const canNext = () => {
    if (step === 1) return label.trim().length > 0;
    if (step === 2) return !!format;
    if (step === 3) return !!courseKey;
    return true;
  };

  const doAuto = () => {
    if (players.length < 2) { toast.error("Need at least 2 players"); return; }
    setGroups(autoGroups(players.map((p) => p.id), groupSizePref));
  };

  const doShuffle = () => {
    if (players.length < 2) { toast.error("Need at least 2 players"); return; }
    setGroups(autoGroups(shuffleIds(players.map((p) => p.id)), groupSizePref));
    toast.success("Groups shuffled");
  };

  const fillUnassigned = () => {
    if (groups.length === 0) return autoGroups(players.map((p) => p.id), groupSizePref);
    const ng = groups.map((g) => ({ ...g, playerIds: [...g.playerIds] }));
    for (const p of unassigned) {
      let target = ng.find((g) => g.playerIds.length < groupSizePref) || ng.find((g) => g.playerIds.length < 4);
      if (!target) {
        target = { playerIds: [], scores: {}, bbb: Array.from({ length: 18 }, () => ({ bingo: null, bango: null, bongo: null })) };
        ng.push(target);
      }
      target.playerIds.push(p.id);
    }
    return ng.filter((g) => g.playerIds.length > 0);
  };

  const goNext = () => {
    if (step === 3 && players.length >= 2 && groups.length === 0) {
      setGroups(autoGroups(players.map((p) => p.id), groupSizePref));
      toast.info("Players auto-assigned to groups — adjust if needed");
    }
    setStep((s) => s + 1);
  };

  const clearGroups = () => setGroups([]);

  const movePlayer = (pid, targetIdx) => {
    setGroups((gs) => {
      let ng = gs.map((g) => ({ ...g, playerIds: g.playerIds.filter((id) => id !== pid) }));
      if (targetIdx < 0) return ng;
      while (ng.length <= targetIdx) {
        ng.push({ playerIds: [], scores: {}, bbb: Array.from({ length: 18 }, () => ({ bingo: null, bango: null, bongo: null })) });
      }
      if (ng[targetIdx].playerIds.length >= 4) { toast.error("Group full (max 4)"); return gs; }
      ng[targetIdx].playerIds = [...ng[targetIdx].playerIds, pid];
      return ng;
    });
  };

  const addInlineCourse = () => {
    const nm = newCourseName.trim();
    if (!nm) { toast.error("Course name required"); return; }
    const key = nm.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 12) + uid().slice(0, 4);
    updateEventLocal((ev) => {
      ev.courses = { ...ev.courses, [key]: { name: nm, subtitle: newCourseSubtitle.trim(), par: Array(18).fill(4), si: Array.from({ length: 18 }, (_, i) => i + 1) } };
      return ev;
    });
    setCourseKey(key);
    setShowNewCourse(false);
    setNewCourseName(""); setNewCourseSubtitle("");
    toast.success(`Added "${nm}"`);
  };

  const importFromLibrary = (entry) => {
    const key = (entry.name || "course").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 12) + uid().slice(0, 4);
    updateEventLocal((ev) => { ev.courses = { ...ev.courses, [key]: entry }; return ev; });
    setCourseKey(key);
    toast.success(`Added "${entry.name}"`);
  };

  const commit = () => {
    if (!courseKey) { toast.error("Pick a course"); setStep(3); return; }
    if (players.length < 2) { toast.error("Add at least 2 players in Setup before creating a day"); setStep(4); return; }
    let finalGroups = groups;
    if (unassigned.length > 0 || groups.length === 0) {
      finalGroups = fillUnassigned();
      setGroups(finalGroups);
      toast.info(`${groups.length === 0 ? players.length : unassigned.length} player${(groups.length === 0 ? players.length : unassigned.length) === 1 ? "" : "s"} auto-assigned to groups`);
    }
    const newDay = {
      id: uid(),
      label: label.trim() || `Day ${(event.days?.length || 0) + 1}`,
      date,
      format,
      courseKey,
      bbbEnabled,
      skinsMode, skinsValue: skinsMode === "off" ? 0 : (skinsValue || 0),
      nassauMode, nassauValue: nassauMode === "off" ? 0 : (nassauValue || 0),
      finalized: false,
      results: null,
      groups: finalGroups,
      handicaps: Object.fromEntries((event.players || []).map((p) => [p.id, p.handicap || 0])),
    };
    updateEventLocal((ev) => {
      ev.days = [...(ev.days || []), newDay];
      return ev;
    });
    onCreated(newDay.id);
    toast.success(`${newDay.label} created`);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="newday-wizard">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[color:var(--bg-dark-green)] border border-[color:var(--border-brass)] rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col tga-fade">
        {/* Header + progress */}
        <div className="px-4 pt-3 pb-2 border-b border-[color:var(--border-brass-light)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--brass-dark)]">Step {step} of {STEPS.length}</div>
              <div className="font-display text-xl text-[color:var(--brass-bright)]">{STEPS[step - 1]}</div>
            </div>
            <button data-testid="newday-close-btn" onClick={onClose} className="ghost-btn rounded-lg p-2" aria-label="Close"><X size={16} /></button>
          </div>
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-1 rounded-full"
                style={{ background: i + 1 <= step ? "var(--brass)" : "rgba(197,160,89,0.2)" }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {step === 1 && (
            <div className="space-y-3" data-testid="newday-step-name">
              <div>
                <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Day name</label>
                <input
                  autoFocus
                  data-testid="newday-label-input"
                  className="tga-input w-full"
                  placeholder="e.g. Day 1, Friday, Championship"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && canNext()) setStep(2); }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Date (optional)</label>
                <input
                  data-testid="newday-date-input"
                  type="date"
                  className="tga-input w-full"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <p className="helptext text-[color:var(--muted-green)]">Give the round a name. You can rename it later.</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="newday-step-format">
              <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Game format</div>
              <div className="space-y-2">
                {FORMATS.map((f) => {
                  const active = format === f.id;
                  return (
                    <button
                      key={f.id}
                      data-testid={`newday-format-${f.id}`}
                      onClick={() => setFormat(f.id)}
                      className="w-full text-left flex items-center gap-3 border rounded-xl px-3 py-2.5 transition"
                      style={{
                        borderColor: active ? "var(--brass)" : "var(--border-brass-light)",
                        background: active ? "rgba(197,160,89,0.10)" : "transparent",
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[color:var(--text-cream)]">{f.label}</div>
                        <div className="text-[11px] text-[color:var(--muted-green)]">{f.desc}</div>
                      </div>
                      {active && <Check size={16} className="text-[color:var(--brass-bright)]" />}
                    </button>
                  );
                })}
              </div>
              <p className="helptext text-[color:var(--muted-green)]">All formats use each player's handicap for net scoring.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="newday-step-course">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Pick a course</div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="newday-course-new-btn"
                    className="ghost-btn rounded-lg px-2 py-1 text-xs inline-flex items-center gap-1"
                    onClick={() => setShowNewCourse((s) => !s)}
                  ><Plus size={12} /> New</button>
                  <button
                    data-testid="newday-course-library-btn"
                    className="ghost-btn rounded-lg px-2 py-1 text-xs inline-flex items-center gap-1"
                    onClick={() => setLibraryOpen(true)}
                  ><BookOpen size={12} /> Library</button>
                </div>
              </div>

              {showNewCourse && (
                <div className="p-3 rounded-lg border border-dashed border-[color:var(--brass-dark)] bg-[rgba(197,160,89,0.06)] space-y-2">
                  <input
                    data-testid="newday-newcourse-name"
                    className="tga-input w-full"
                    placeholder="Course name"
                    autoFocus
                    value={newCourseName}
                    onChange={(e) => setNewCourseName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addInlineCourse(); }}
                  />
                  <input
                    data-testid="newday-newcourse-subtitle"
                    className="tga-input w-full"
                    placeholder="Subtitle (optional, e.g. Club · City)"
                    value={newCourseSubtitle}
                    onChange={(e) => setNewCourseSubtitle(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button data-testid="newday-newcourse-save" className="brass-btn rounded-lg px-3 py-1 text-sm" onClick={addInlineCourse}>Add course</button>
                    <button className="ghost-btn rounded-lg px-3 py-1 text-sm" onClick={() => setShowNewCourse(false)}>Cancel</button>
                  </div>
                  <p className="helptext text-[color:var(--muted-green)]">Set Par and Stroke Index later from the Setup tab.</p>
                </div>
              )}

              {Object.keys(event.courses || {}).length === 0 ? (
                <div className="tga-card-dark text-center py-6">
                  <p className="helptext">No courses yet. Add one or import from the library.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(event.courses || {}).map(([key, c]) => {
                    const active = courseKey === key;
                    return (
                      <button
                        key={key}
                        data-testid={`newday-course-option-${key}`}
                        onClick={() => setCourseKey(key)}
                        className="w-full text-left flex items-center gap-3 border rounded-xl px-3 py-2.5 transition"
                        style={{
                          borderColor: active ? "var(--brass)" : "var(--border-brass-light)",
                          background: active ? "rgba(197,160,89,0.10)" : "transparent",
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[color:var(--text-cream)] truncate">{c.name}</div>
                          {c.subtitle && <div className="text-[11px] text-[color:var(--muted-green)] truncate">{c.subtitle}</div>}
                        </div>
                        {active && <Check size={16} className="text-[color:var(--brass-bright)]" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="newday-step-groups">
              {players.length < 2 ? (
                <div className="tga-card-dark text-center py-6">
                  <p className="helptext">Add at least 2 players from the Setup tab before creating a day — every player must be in a playing group.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Playing groups ({players.length} players)</div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-[color:var(--muted-green)]">Size</label>
                      <select
                        data-testid="newday-groupsize-select"
                        className="tga-input !w-16 !py-1"
                        value={groupSizePref}
                        onChange={(e) => setGroupSizePref(parseInt(e.target.value))}
                      >
                        {[2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <button data-testid="newday-auto-assign" className="brass-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" onClick={doAuto}>
                        <Wand2 size={14}/> Auto
                      </button>
                      <button data-testid="newday-shuffle-groups" title="Fresh random pairing" className="ghost-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" onClick={doShuffle}>
                        <Shuffle size={14}/> Shuffle
                      </button>
                      <button data-testid="newday-clear-groups" className="ghost-btn rounded-lg px-3 py-1 text-xs" onClick={clearGroups}>Clear</button>
                    </div>
                  </div>

                  {groups.length === 0 ? (
                    <p className="helptext text-[color:var(--muted-green)]">No groups yet. Tap Auto to split evenly, or assign each player below.</p>
                  ) : (
                    <div className="space-y-2">
                      {groups.map((g, gi) => (
                        <div key={gi} className="border border-[color:var(--border-brass-light)] rounded-xl p-3">
                          <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">Group {String.fromCharCode(65 + gi)} ({g.playerIds.length}/4)</div>
                          <div className="flex flex-wrap gap-2">
                            {g.playerIds.map((pid) => {
                              const p = players.find((x) => x.id === pid);
                              if (!p) return null;
                              return (
                                <div key={pid} className="inline-flex items-center gap-2 bg-[rgba(197,160,89,0.08)] rounded-full pl-1 pr-2 py-1">
                                  <div className="avatar avatar-dark" style={{ width: 24, height: 24, fontSize: 11 }}>{initials(p.name)}</div>
                                  <span className="text-sm text-[color:var(--text-cream)]">{p.name}</span>
                                  <button
                                    data-testid={`newday-remove-from-group-${pid}`}
                                    className="text-[color:var(--brass-dark)] hover:text-red-400 text-xs"
                                    onClick={() => movePlayer(pid, -1)}
                                    aria-label="Remove"
                                  >✕</button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {unassigned.length > 0 && (
                    <div className="border-t border-dashed border-[color:var(--border-brass-light)] pt-3">
                      <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">Unassigned ({unassigned.length})</div>
                      <div className="flex flex-wrap gap-2">
                        {unassigned.map((p) => (
                          <div key={p.id} className="inline-flex items-center gap-2 bg-[rgba(0,0,0,0.15)] border border-dashed border-[color:var(--border-brass-light)] rounded-full pl-1 pr-2 py-1">
                            <div className="avatar avatar-dark" style={{ width: 24, height: 24, fontSize: 11 }}>{initials(p.name)}</div>
                            <span className="text-sm text-[color:var(--text-cream)]">{p.name}</span>
                            <select
                              data-testid={`newday-assign-${p.id}`}
                              className="text-xs bg-transparent border border-[color:var(--border-brass-light)] rounded px-1 py-0.5 text-[color:var(--text-cream)]"
                              value=""
                              onChange={(e) => { if (e.target.value !== "") movePlayer(p.id, parseInt(e.target.value)); }}
                            >
                              <option value="" style={{ color: "black" }}>Add to…</option>
                              {groups.map((_, i) => <option key={i} value={i} style={{ color: "black" }}>Group {String.fromCharCode(65 + i)}</option>)}
                              <option value={groups.length} style={{ color: "black" }}>+ New group</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="newday-step-sidegames">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-[color:var(--text-cream)]">Bingo · Bango · Bongo</div>
                  <div className="helptext text-[color:var(--muted-green)]">Side game points per hole</div>
                </div>
                <div className="pill-toggle">
                  <button data-testid="newday-bbb-on" className={bbbEnabled ? "on" : ""} onClick={() => setBbbEnabled(true)}>On</button>
                  <button data-testid="newday-bbb-off" className={!bbbEnabled ? "on" : ""} onClick={() => setBbbEnabled(false)}>Off</button>
                </div>
              </div>

              <div className="border-t border-[color:var(--border-brass-light)] pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[color:var(--text-cream)]">Skins</div>
                    <div className="helptext text-[color:var(--muted-green)]">Lowest score wins the hole; ties carry over</div>
                  </div>
                  <div className="pill-toggle">
                    <button data-testid="newday-skins-off" className={skinsMode === "off" ? "on" : ""} onClick={() => setSkinsMode("off")}>Off</button>
                    <button data-testid="newday-skins-gross" className={skinsMode === "gross" ? "on" : ""} onClick={() => setSkinsMode("gross")}>Gross</button>
                    <button data-testid="newday-skins-net" className={skinsMode === "net" ? "on" : ""} onClick={() => setSkinsMode("net")}>Net</button>
                  </div>
                </div>
                {skinsMode !== "off" && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-[color:var(--muted-green)] flex-1">Value per skin</label>
                    <div className="flex items-center gap-1 border border-[color:var(--border-brass-light)] rounded-lg px-2 py-1 bg-[rgba(0,0,0,0.2)]">
                      <span className="font-mono-metrics text-sm text-[color:var(--brass)]">{event.currency || "$"}</span>
                      <input
                        data-testid="newday-skins-value"
                        type="number"
                        min="0"
                        step="1"
                        className="tga-input !border-0 !p-0 !py-0 w-20 text-right font-mono-metrics bg-transparent"
                        value={skinsValue}
                        onChange={(e) => setSkinsValue(Math.max(0, parseFloat(e.target.value) || 0))}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-[color:var(--border-brass-light)] pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[color:var(--text-cream)]">Nassau</div>
                    <div className="helptext text-[color:var(--muted-green)]">3 bets · Front 9 · Back 9 · Overall</div>
                  </div>
                  <div className="pill-toggle">
                    <button data-testid="newday-nassau-off" className={nassauMode === "off" ? "on" : ""} onClick={() => setNassauMode("off")}>Off</button>
                    <button data-testid="newday-nassau-gross" className={nassauMode === "gross" ? "on" : ""} onClick={() => setNassauMode("gross")}>Gross</button>
                    <button data-testid="newday-nassau-net" className={nassauMode === "net" ? "on" : ""} onClick={() => setNassauMode("net")}>Net</button>
                  </div>
                </div>
                {nassauMode !== "off" && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-[color:var(--muted-green)] flex-1">Value per segment</label>
                    <div className="flex items-center gap-1 border border-[color:var(--border-brass-light)] rounded-lg px-2 py-1 bg-[rgba(0,0,0,0.2)]">
                      <span className="font-mono-metrics text-sm text-[color:var(--brass)]">{event.currency || "$"}</span>
                      <input
                        data-testid="newday-nassau-value"
                        type="number"
                        min="0"
                        step="1"
                        className="tga-input !border-0 !p-0 !py-0 w-20 text-right font-mono-metrics bg-transparent"
                        value={nassauValue}
                        onChange={(e) => setNassauValue(Math.max(0, parseFloat(e.target.value) || 0))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[color:var(--border-brass-light)] flex items-center gap-2">
          {step > 1 ? (
            <button
              data-testid="newday-back-btn"
              className="ghost-btn rounded-lg px-3 py-2 text-sm inline-flex items-center gap-1"
              onClick={() => setStep((s) => s - 1)}
            ><ChevronLeft size={14} /> Back</button>
          ) : (
            <button
              data-testid="newday-cancel-btn"
              className="ghost-btn rounded-lg px-3 py-2 text-sm"
              onClick={onClose}
            >Cancel</button>
          )}
          <div className="flex-1" />
          {step < STEPS.length ? (
            <button
              data-testid="newday-next-btn"
              disabled={!canNext()}
              className="brass-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50"
              onClick={goNext}
            >Next <ChevronRight size={14} /></button>
          ) : (
            <button
              data-testid="newday-create-btn"
              className="brass-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1"
              onClick={commit}
            ><Check size={14} /> Create day</button>
          )}
        </div>
      </div>

      <CourseLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onImport={importFromLibrary}
        updateEventLocal={updateEventLocal}
      />
    </div>,
    document.body
  );
}
